import { useCallback, useEffect } from 'react'
import { GlobalState, useGlobalEvent } from '@highlight/ipc'
import { useReducedMotion } from 'framer-motion'

import { TourCard } from './nested/tour-card'
import { useTourSteps } from './use-tour-steps'
import { tourTargetElement, useTourTargetPositions } from './use-tour-target-positions'
import { useQuickTourActiveAtom, useQuickTourStepAtom, useSetQuickTourCompleted, useStartQuickTour } from '../../atoms'

interface QuickTourProps {
  isSidepanelOpen: boolean
}

/** Where a spotlighted section is scrolled to, measured from the body viewport top. */
const SECTION_TOP_PADDING = 200
/** Don't re-scroll for a section that is already close enough to its mark. */
const SCROLL_TOLERANCE = 24
const SCROLL_DURATION_MS = 400

function findScrollableParent(el: Element): HTMLElement | null {
  let current: HTMLElement | null = el.parentElement
  while (current) {
    const overflowY = getComputedStyle(current).overflowY
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') &&
      current.scrollHeight > current.clientHeight
    ) {
      return current
    }
    current = current.parentElement
  }
  return null
}

function smoothScrollTo(el: HTMLElement, target: number, durationMs = SCROLL_DURATION_MS) {
  const start = el.scrollTop
  const delta = target - start
  if (Math.abs(delta) < 1) return
  const startTime = performance.now()

  const step = (now: number) => {
    const t = Math.min(1, (now - startTime) / durationMs)
    const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
    el.scrollTop = start + delta * eased
    if (t < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

export function QuickTour({ isSidepanelOpen }: QuickTourProps) {
  const [isActive, setIsActive] = useQuickTourActiveAtom()
  const [currentStep, setCurrentStep] = useQuickTourStepAtom()
  const setTourCompleted = useSetQuickTourCompleted()
  const startQuickTour = useStartQuickTour()
  const prefersReducedMotion = useReducedMotion() ?? false

  const steps = useTourSteps()
  const isVisible = isActive && isSidepanelOpen

  const normalizedStep = Math.min(Math.max(currentStep, 0), steps.length - 1)
  const activeStep = steps[normalizedStep]
  const isLastStep = normalizedStep === steps.length - 1

  const targetCenters = useTourTargetPositions(
    isVisible,
    steps.map((step) => step.target),
  )

  // Bring the spotlighted section into view. The toolbar and chat input are
  // fixed chrome, so those steps just return the body to the top.
  useEffect(() => {
    if (!isVisible) return

    const anchor = tourTargetElement(activeStep.target) ?? tourTargetElement('actions')
    if (!anchor) return
    const scrollParent = findScrollableParent(anchor)
    if (!scrollParent) return

    const scrollTo = (top: number) => {
      if (prefersReducedMotion) {
        scrollParent.scrollTop = top
        return
      }
      smoothScrollTo(scrollParent, top)
    }

    if (activeStep.target === 'toolbar' || activeStep.target === 'chat') {
      scrollTo(0)
      return
    }

    const rect = anchor.getBoundingClientRect()
    const targetViewportTop = scrollParent.getBoundingClientRect().top + SECTION_TOP_PADDING
    if (Math.abs(rect.top - targetViewportTop) < SCROLL_TOLERANCE) return
    // Clamping at 0 covers the section the user put first — it can't scroll up
    // to the mark, and shouldn't try.
    const maxScroll = scrollParent.scrollHeight - scrollParent.clientHeight
    const target = scrollParent.scrollTop + rect.top - targetViewportTop
    scrollTo(Math.min(Math.max(0, target), maxScroll))
  }, [isVisible, activeStep.target, prefersReducedMotion])

  // Cross-process start (e.g. from the settings window's developer tab).
  useGlobalEvent('quickTour:start', startQuickTour)

  // Sync isActive state to GlobalState so main process can check it
  useEffect(() => {
    GlobalState.set('sidepanel.quickTourActive', isVisible)
    return () => {
      GlobalState.set('sidepanel.quickTourActive', false)
    }
  }, [isVisible])

  // The panel's content is a read-only stage for the duration of the tour.
  // `inert` is what makes that true: `pointer-events-none` on an ancestor
  // doesn't hold, because the body and the footer's controls set
  // `pointer-events-auto` back on themselves, and it never blocks focus or
  // typing anyway. Inerting also blurs whatever held focus (the chat input's
  // caret). The tour card is a sibling of the panel, so it stays interactive.
  //
  // The content node, not the panel root: the resize handles and the tour's
  // drag strip are siblings of it, and step 1 teaches resizing and dragging, so
  // they have to stay live.
  useEffect(() => {
    if (!isVisible) return
    const content = document.querySelector<HTMLElement>('[data-sidepanel-content]')
    if (!content) return
    content.inert = true
    return () => {
      content.inert = false
    }
  }, [isVisible])

  const handleComplete = useCallback(() => {
    setIsActive(false)
    setCurrentStep(0)
    setTourCompleted(true)
  }, [setIsActive, setCurrentStep, setTourCompleted])

  const handleSkip = useCallback(() => {
    handleComplete()
  }, [handleComplete])

  const handleNext = useCallback(() => {
    if (isLastStep) {
      handleComplete()
    } else {
      setCurrentStep(normalizedStep + 1)
    }
  }, [isLastStep, normalizedStep, setCurrentStep, handleComplete])

  const handlePrev = useCallback(() => {
    if (normalizedStep > 0) {
      setCurrentStep(normalizedStep - 1)
    }
  }, [normalizedStep, setCurrentStep])

  // Keyboard navigation
  useEffect(() => {
    if (!isVisible) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          handleSkip()
          break
        case 'Enter':
        case 'ArrowRight':
          handleNext()
          break
        case 'ArrowLeft':
          handlePrev()
          break
        case 'ArrowUp':
        case 'ArrowDown':
        case 'PageUp':
        case 'PageDown':
        case 'Home':
        case 'End':
        case ' ':
          e.preventDefault()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isVisible, handleNext, handlePrev, handleSkip])

  if (!isVisible) return null

  // No screen scrim: the designs leave the desktop at full brightness and let
  // the lit section plus the floating card carry the focus.
  return (
    <TourCard
      step={activeStep}
      stepIndex={normalizedStep}
      totalSteps={steps.length}
      anchorY={targetCenters[activeStep.target] ?? null}
      isFirstStep={normalizedStep === 0}
      isLastStep={isLastStep}
      prefersReducedMotion={prefersReducedMotion}
      onSkip={handleSkip}
      onPrev={handlePrev}
      onNext={handleNext}
    />
  )
}
